/**
 * ScheduleSendDialog
 * ------------------
 * Per-template "Schedule this email" modal. Opens from a button on each
 * email TemplateCard inside EmailCampaigns. Picks date/time/timezone and
 * audience type, then POSTs to /api/email-schedules.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  CalendarClock,
  Sparkles,
  Loader2,
  CheckCircle2,
  Users,
  Building2,
  Truck,
  Package,
  CalendarIcon,
} from "lucide-react";
import { API } from "@/App";

const TIMEZONES = [
  { v: "America/New_York", l: "Eastern (ET)" },
  { v: "America/Chicago", l: "Central (CT)" },
  { v: "America/Denver", l: "Mountain (MT)" },
  { v: "America/Los_Angeles", l: "Pacific (PT)" },
];

const AUDIENCES = [
  { v: "hospitals", l: "All Hospital Users", icon: Building2, color: "text-blue-600" },
  { v: "contractors", l: "All Contractors", icon: Truck, color: "text-green-600" },
  { v: "suppliers", l: "All Suppliers", icon: Package, color: "text-orange-600" },
  { v: "all", l: "All Users (Hospitals + Contractors + Suppliers)", icon: Users, color: "text-purple-600" },
  { v: "custom", l: "Custom email list…", icon: Users, color: "text-slate-600" },
];

function defaultDate() {
  // tomorrow at 10:00 local
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return d;
}

function pad(n) {
  return String(n).padStart(2, "0");
}

function toLocalISO(date, hh, mm) {
  // Build a naive local ISO (no tz suffix). Server combines with the chosen timezone.
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(hh)}:${pad(mm)}:00`;
}

export default function ScheduleSendDialog({ open, onOpenChange, template, token, onScheduled }) {
  const headers = { Authorization: `Bearer ${token}` };

  const [date, setDate] = useState(defaultDate());
  const [hour, setHour] = useState(10);
  const [minute, setMinute] = useState(0);
  const [timezone, setTimezone] = useState("America/New_York");
  const [audience, setAudience] = useState("hospitals");
  const [customEmails, setCustomEmails] = useState("");
  const [subjectOverride, setSubjectOverride] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset when re-opened
  useEffect(() => {
    if (open) {
      setDate(defaultDate());
      setHour(10);
      setMinute(0);
      setTimezone("America/New_York");
      setAudience("hospitals");
      setCustomEmails("");
      setSubjectOverride("");
      setAiSuggestion(null);
    }
  }, [open]);

  const dateLabel = useMemo(
    () =>
      date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    [date]
  );

  const askAi = async () => {
    setAiBusy(true);
    setAiSuggestion(null);
    try {
      const r = await fetch(`${API}/email-schedules/ai-suggest-time`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          target_audience: audience === "custom" ? "general" : audience,
          template_id: template?.id,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "AI suggestion failed");
      const sug = data.suggestion;
      setAiSuggestion(sug);
      // Apply suggested time
      if (sug?.best_time) {
        const [hh, mm] = sug.best_time.split(":").map((x) => parseInt(x, 10));
        if (!Number.isNaN(hh)) setHour(hh);
        if (!Number.isNaN(mm)) setMinute(mm);
      }
      if (sug?.timezone) setTimezone(sug.timezone);
      // Move date to next occurrence of suggested weekday
      if (sug?.best_day_of_week) {
        const targetDow = [
          "sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday",
        ].indexOf(sug.best_day_of_week.toLowerCase());
        if (targetDow >= 0) {
          const d = new Date();
          d.setHours(0, 0, 0, 0);
          const diff = (targetDow - d.getDay() + 7) % 7 || 7;
          d.setDate(d.getDate() + diff);
          setDate(d);
        }
      }
      toast.success("AI picked the best time");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAiBusy(false);
    }
  };

  const submit = async () => {
    if (!template) return;
    setSubmitting(true);
    try {
      const body = {
        name: `${template.name} — ${dateLabel} ${pad(hour)}:${pad(minute)}`,
        template_id: template.id,
        template_name: template.name,
        subject_override: subjectOverride || undefined,
        scheduled_for: toLocalISO(date, hour, minute),
        timezone,
      };
      if (audience === "custom") {
        const list = customEmails
          .split(/[\s,;]+/)
          .map((e) => e.trim().toLowerCase())
          .filter((e) => /.+@.+\..+/.test(e));
        if (list.length === 0) {
          toast.error("Add at least one valid email");
          setSubmitting(false);
          return;
        }
        body.recipients = list.map((e) => ({ email: e, name: e.split("@")[0] }));
      } else {
        body.recipient_type = audience;
      }
      const r = await fetch(`${API}/email-schedules`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed to schedule");
      toast.success(`Scheduled · ${data.id}`);
      onScheduled && onScheduled(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="schedule-send-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-violet-900">
            <CalendarClock className="w-5 h-5" />
            Schedule “{template.name}”
          </DialogTitle>
          <DialogDescription>
            Pick the date, time and audience. The campaign will be dispatched automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-2">
          {/* LEFT — date */}
          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Send date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                  data-testid="schedule-date-trigger"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => d && setDate(d)}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <Label className="text-xs uppercase tracking-wider text-slate-500 pt-2">Send time</Label>
            <div className="flex items-center gap-2">
              <Select value={String(hour)} onValueChange={(v) => setHour(parseInt(v, 10))}>
                <SelectTrigger className="w-24" data-testid="schedule-hour">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }).map((_, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {pad(i)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-slate-500">:</span>
              <Select value={String(minute)} onValueChange={(v) => setMinute(parseInt(v, 10))}>
                <SelectTrigger className="w-24" data-testid="schedule-minute">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[0, 15, 30, 45].map((m) => (
                    <SelectItem key={m} value={String(m)}>
                      {pad(m)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger className="flex-1" data-testid="schedule-tz">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((t) => (
                    <SelectItem key={t.v} value={t.v}>
                      {t.l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={askAi}
              disabled={aiBusy}
              className="w-full text-violet-700 border-violet-200 hover:bg-violet-50 mt-1"
              data-testid="schedule-ai-suggest-btn"
            >
              {aiBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              AI: pick best time for this audience
            </Button>
            {aiSuggestion && (
              <div className="text-xs bg-violet-50 border border-violet-200 rounded p-2 text-violet-900">
                <div className="flex items-center gap-1 font-semibold mb-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Suggested: {aiSuggestion.best_day_of_week} {aiSuggestion.best_time}{" "}
                  {aiSuggestion.timezone && (
                    <Badge variant="outline" className="ml-1 text-[10px]">
                      {aiSuggestion.timezone}
                    </Badge>
                  )}
                </div>
                <p className="text-violet-700 leading-snug">{aiSuggestion.reason}</p>
              </div>
            )}
          </div>

          {/* RIGHT — audience + subject */}
          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Send to</Label>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger data-testid="schedule-audience">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUDIENCES.map((a) => {
                  const Icon = a.icon;
                  return (
                    <SelectItem key={a.v} value={a.v}>
                      <span className="flex items-center gap-2">
                        <Icon className={`w-3 h-3 ${a.color}`} />
                        {a.l}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            {audience === "custom" && (
              <div>
                <Label className="text-xs text-slate-500">
                  Paste emails (commas, spaces, or new lines)
                </Label>
                <textarea
                  value={customEmails}
                  onChange={(e) => setCustomEmails(e.target.value)}
                  rows={4}
                  placeholder="jane@hospital.org, john@clinic.com"
                  className="w-full mt-1 border border-slate-300 rounded p-2 text-sm font-mono"
                  data-testid="schedule-custom-emails"
                />
              </div>
            )}

            <Label className="text-xs uppercase tracking-wider text-slate-500 pt-2">
              Subject override (optional)
            </Label>
            <Input
              value={subjectOverride}
              onChange={(e) => setSubjectOverride(e.target.value)}
              placeholder={template.subject || "Use template default"}
              data-testid="schedule-subject"
            />

            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-600 leading-relaxed">
              <strong className="text-slate-800">Template:</strong> {template.name}
              <br />
              <strong className="text-slate-800">Sending at:</strong> {dateLabel} · {pad(hour)}:
              {pad(minute)} {TIMEZONES.find((t) => t.v === timezone)?.l}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="schedule-confirm-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CalendarClock className="w-4 h-4 mr-2" />
            )}
            Schedule send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
